import { useEffect, useRef, type ComponentRef, type MutableRefObject } from 'react';
import {
  Controller,
  type Control,
  type FieldErrors,
  type UseFormSetValue,
  type UseFormWatch,
} from 'react-hook-form';
import {
  AppButton,
  AppForm,
  AppFormLabel,
  AppRadio,
  AppRadioGroup,
  AppTextField,
} from '@/components/atoms';
import { RA2NDatePicker } from '@/components/organisms';
import { normalizeAddress, normalizePostalCode } from '@/utils';
import { dateValueToIso } from '@/utils/dateConverter';
import type { UseJapaneseNameKanaFieldsResult } from '@/hooks/useJapaneseNameKanaFields';
import {
  PATIENT_ADDRESS1_MAX_LENGTH,
  PATIENT_ADDRESS2_MAX_LENGTH,
  PATIENT_ADDRESS_KANA_MAX_LENGTH,
  PATIENT_TELEPHONE_MAX_LENGTH,
  type PatientFormSchemaType,
} from '@pages/main/patient/types';
import {
  normalizePaddedInsurerNumber as normalizeInsurerNumber,
  normalizePostalDigits as normalizeInsurancePostalDigits,
} from './PatientSectionShared.helpers';

type AppTextFieldRef = ComponentRef<typeof AppTextField>;
type BasicOption = { id: number; name: string };
type CostOption = BasicOption & { value?: string };

interface HealthInsuranceSectionFormProps {
  control: Control<PatientFormSchemaType>;
  watch: UseFormWatch<PatientFormSchemaType>;
  setValue: UseFormSetValue<PatientFormSchemaType>;
  errors: FieldErrors<PatientFormSchemaType>;
  relationshipId: number | null | undefined;
  insuranceTypeId: number | null | undefined;
  patientCostId: number | null | undefined;
  elderlyInsuranceCertificateId: number | null | undefined;
  insurerName: string;
  costRateSelectionValue: string;
  isReimbursementInsurer: boolean;
  isSeikatsuHogo: boolean;
  isInsuredPerson: boolean;
  disabled: boolean;
  isInsurerSearchLoading: boolean;
  onInsurerSearch: () => void;
  onAddressSearch: () => void;
  onOpenMap: () => void;
  relationshipOptions: BasicOption[];
  insuranceTypeOptions: BasicOption[];
  patientCostOptions: CostOption[];
  elderlyOptions: CostOption[];
  sexOptions: BasicOption[];
  getInitialPatientCostRate: (nextRelationshipId?: number | null) => string;
  getFieldReadonly: (fieldType: 'cardMark' | 'cardNumber') => boolean;
  getFieldDisabled: () => boolean;
  getCardNumberLabel: () => string;
  handleCardMarkBlur: () => void;
  handleCardNumberBlur: () => void;
  insuranceTypeChangedByUserRef: MutableRefObject<boolean>;
  cardNumberInputRef: MutableRefObject<AppTextFieldRef | null>;
  cardMarkInputRef: MutableRefObject<AppTextFieldRef | null>;
  insuredNameKanaMaxLength: number;
  insuredNameInputProps: UseJapaneseNameKanaFieldsResult['nameInputProps'];
  insuredNameKanaInputProps: UseJapaneseNameKanaFieldsResult['kanaInputProps'];
  address2Ref?: MutableRefObject<{ focus?: () => void } | null>;
}

export function HealthInsuranceSectionForm({
  control,
  watch,
  setValue,
  errors,
  relationshipId,
  insuranceTypeId,
  patientCostId,
  elderlyInsuranceCertificateId,
  insurerName,
  costRateSelectionValue,
  isReimbursementInsurer,
  isSeikatsuHogo,
  isInsuredPerson,
  disabled,
  isInsurerSearchLoading,
  onInsurerSearch,
  onAddressSearch,
  onOpenMap,
  relationshipOptions,
  insuranceTypeOptions,
  patientCostOptions,
  elderlyOptions,
  sexOptions,
  getInitialPatientCostRate,
  getFieldReadonly,
  getFieldDisabled,
  getCardNumberLabel,
  handleCardMarkBlur,
  handleCardNumberBlur,
  insuranceTypeChangedByUserRef,
  cardNumberInputRef,
  cardMarkInputRef,
  insuredNameKanaMaxLength,
  insuredNameInputProps,
  insuredNameKanaInputProps,
  address2Ref,
}: HealthInsuranceSectionFormProps) {
  const insurancePostalCode2Ref = useRef<AppTextFieldRef | null>(null);
  const prevInsurancePc1DigitsLenRef = useRef(0);
  const insurancePostalCode1Value = watch('health_insurance_card.postal_code1');

  useEffect(() => {
    prevInsurancePc1DigitsLenRef.current = normalizeInsurancePostalDigits(
      insurancePostalCode1Value || ''
    ).length;
  }, [insurancePostalCode1Value]);

  const handlePostalCode1Blur = (value: string) => {
    const normalized = normalizePostalCode(value).slice(0, 3);
    if (normalized !== value) {
      setValue('health_insurance_card.postal_code1', normalized);
    }
  };

  const handlePostalCode2Blur = (value: string) => {
    const normalized = normalizePostalCode(value).slice(0, 4);
    if (normalized !== value) {
      setValue('health_insurance_card.postal_code2', normalized);
    }
  };

  const handleTelephoneBlur = (value: string) => {
    if (!value) return;
    const s = String(value);
    const normalized = s
      .replace(/[０-９]/g, c => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
      .replace(/[－ー―ｰ]/g, '-');
    if (normalized !== value) {
      setValue('health_insurance_card.telephone', normalized);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
      <div className="grid grid-cols-[96px_minmax(0,1fr)] items-start gap-x-3 gap-y-2 sm:grid-cols-[140px_minmax(0,1fr)] sm:gap-x-6">
        <AppFormLabel label="続柄" required />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <AppForm.Item
            className="mb-0"
            validateStatus={errors.health_insurance_card?.relationship_id ? 'error' : undefined}
            help={errors.health_insurance_card?.relationship_id?.message as any}
          >
            <AppRadioGroup
              value={relationshipId}
              onChange={e => {
                const nextRid = e.target.value;
                setValue('health_insurance_card.relationship_id', nextRid);
                const pName = watch('patient_information.name') || '';
                const pKana = watch('patient_information.name_kana') || '';
                if (nextRid === 2) {
                  setValue('health_insurance_card.name', pName.split(/[\s　]+/)[0] || '');
                  setValue('health_insurance_card.name_kana', pKana.split(/[\s　]+/)[0] || '');
                } else {
                  setValue('health_insurance_card.name', pName);
                  setValue('health_insurance_card.name_kana', pKana);
                }

                const isInitialSelectionActive =
                  (patientCostId === undefined || patientCostId === null || patientCostId === 0) &&
                  (elderlyInsuranceCertificateId ?? 1) <= 1;
                if (isInitialSelectionActive) {
                  setValue(
                    'health_insurance_card.patient_cost_rate',
                    getInitialPatientCostRate(nextRid)
                  );
                }
              }}
            >
              {relationshipOptions.map(rel => (
                <AppRadio key={rel.id} value={rel.id}>
                  {rel.name}
                </AppRadio>
              ))}
            </AppRadioGroup>
          </AppForm.Item>
          {isReimbursementInsurer && (
            <span className="shrink-0 rounded-sm border border-gray-300 bg-[#eeeeee] px-1 py-0.5 text-xl">
              償還払い
            </span>
          )}
        </div>

        <AppFormLabel label="保険種別" required />
        <div>
          <AppForm.Item
            className="mb-0"
            validateStatus={errors.health_insurance_card?.insurance_type_id ? 'error' : undefined}
            help={errors.health_insurance_card?.insurance_type_id?.message as any}
          >
            <AppRadioGroup
              value={insuranceTypeId}
              onChange={e => {
                insuranceTypeChangedByUserRef.current = true;
                setValue('health_insurance_card.insurance_type_id', e.target.value);
              }}
            >
              {insuranceTypeOptions.map(type => (
                <AppRadio key={type.id} value={type.id}>
                  {type.name}
                </AppRadio>
              ))}
            </AppRadioGroup>
          </AppForm.Item>
        </div>

        <AppFormLabel label="保険者番号" required />
        <div>
          <div className="flex flex-wrap items-start gap-2">
            <div className="min-w-[8rem]">
              <Controller
                name="health_insurance_card.insurer_number"
                control={control}
                render={({ field, fieldState }) => (
                  <AppForm.Item
                    className="mb-0"
                    validateStatus={fieldState.invalid ? 'error' : undefined}
                    help={fieldState.error?.message}
                  >
                    <AppTextField
                      {...field}
                      value={field.value ?? ''}
                      placeholder="00000000"
                      maxLength={10}
                      className={isReimbursementInsurer ? 'border-pink-500' : ''}
                      status={fieldState.invalid ? 'error' : undefined}
                      disabled={isSeikatsuHogo || disabled}
                      onChange={e => {
                        const value = e.target.value.slice(0, 10);
                        field.onChange(value);
                        setValue('health_insurance_card.claim_type_id', undefined);
                      }}
                      onPressEnter={e => {
                        const rawValue = (e.currentTarget.value ?? '').replace(/[０-９]/g, c =>
                          String.fromCharCode(c.charCodeAt(0) - 0xfee0)
                        );
                        const normalized = normalizeInsurerNumber(rawValue);
                        let predictedType = watch('health_insurance_card.insurance_type_id');
                        if (normalized) {
                          if (normalized === '00000001') predictedType = 3;
                          else if (normalized.startsWith('39')) predictedType = 2;
                          else predictedType = 1;
                        }

                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.blur();
                        setTimeout(() => {
                          if (predictedType === 2) {
                            cardNumberInputRef.current?.focus?.();
                          } else {
                            cardMarkInputRef.current?.focus?.();
                          }
                        }, 0);
                      }}
                      onBlur={e => {
                        let value = e.target.value || '';
                        value = value.replace(/[０-９]/g, c =>
                          String.fromCharCode(c.charCodeAt(0) - 0xfee0)
                        );

                        const normalized = normalizeInsurerNumber(value);
                        if (normalized !== (field.value ?? '')) {
                          setValue('health_insurance_card.insurer_number', normalized, {
                            shouldDirty: true,
                            shouldTouch: true,
                            shouldValidate: false,
                          });
                        }
                        field.onBlur();

                        if (!normalized) {
                          setValue('health_insurance_card.insurer_name', '');
                          setValue('health_insurance_card.insurer_kind_name', '');
                          setValue('health_insurance_card.patient_cost_rate', '');
                          setValue('health_insurance_card.patient_cost_id', undefined);
                          setValue('health_insurance_card.claim_type_id', undefined);
                          setValue('health_insurance_card.cost_rate', undefined);
                          setValue('health_insurance_card.family_cost_rate', undefined);
                          setValue(
                            'health_insurance_card.elderly_insurance_certificate_id',
                            undefined
                          );
                        } else {
                          const currentInsuranceTypeId = watch(
                            'health_insurance_card.insurance_type_id'
                          );
                          if (normalized === '00000001') {
                            if (currentInsuranceTypeId !== 3) {
                              setValue('health_insurance_card.insurance_type_id', 3);
                            }
                          } else if (normalized.startsWith('39')) {
                            if (currentInsuranceTypeId !== 2) {
                              setValue('health_insurance_card.insurance_type_id', 2);
                            }
                          } else if (currentInsuranceTypeId !== 1) {
                            setValue('health_insurance_card.insurance_type_id', 1);
                          }
                          onInsurerSearch();
                        }
                      }}
                    />
                  </AppForm.Item>
                )}
              />
            </div>
            <AppForm.Item
              className="mb-0 min-w-[10rem] flex-1"
              validateStatus={errors.health_insurance_card?.insurer_kind_name ? 'error' : undefined}
              help={errors.health_insurance_card?.insurer_kind_name?.message as any}
            >
              <AppTextField
                value={
                  (watch(
                    'health_insurance_card.insurer_kind_name' as keyof PatientFormSchemaType
                  ) as unknown as string) || ''
                }
                disabled
                className="bg-gray-100"
                status={errors.health_insurance_card?.insurer_kind_name ? 'error' : undefined}
              />
            </AppForm.Item>
            <AppButton
              type="primary"
              onClick={onInsurerSearch}
              loading={isInsurerSearchLoading}
              data-skip-enter-nav
              disabled={disabled}
            >
              保険者検索
            </AppButton>
          </div>
        </div>

        <AppFormLabel label="保険者名称" required />
        <div>
          <AppForm.Item
            className="mb-0"
            validateStatus={errors.health_insurance_card?.insurer_name ? 'error' : undefined}
            help={errors.health_insurance_card?.insurer_name?.message as any}
          >
            <AppTextField
              value={insurerName}
              disabled
              className="w-full bg-gray-100"
              status={errors.health_insurance_card?.insurer_name ? 'error' : undefined}
            />
          </AppForm.Item>
        </div>

        <AppFormLabel label="受診者負担" />
        <div className="flex items-center gap-2">
          <AppTextField
            value={
              (watch(
                'health_insurance_card.patient_cost_rate' as keyof PatientFormSchemaType
              ) as unknown as string) || ''
            }
            disabled
            className="w-16 bg-gray-100"
          />
          <span className="text-sm">割</span>
        </div>

        <div></div>
        <div className="grid grid-cols-[96px_minmax(0,1fr)] items-start gap-x-3 gap-y-2 sm:grid-cols-[120px_minmax(0,1fr)]">
          <AppFormLabel label="負担割合変更" className="!whitespace-normal" />
          <div className="flex flex-wrap items-center gap-2">
            <AppRadioGroup
              data-skip-enter-nav
              value={costRateSelectionValue}
              onChange={e => {
                const selectedValue = String(e.target.value || '');

                if (selectedValue.startsWith('pc-')) {
                  const val = Number(selectedValue.replace('pc-', ''));
                  setValue('health_insurance_card.patient_cost_id', val);
                  setValue('health_insurance_card.elderly_insurance_certificate_id', 1);

                  let costRate = '';
                  if (val === 0) {
                    costRate = getInitialPatientCostRate();
                  } else {
                    const costOption = patientCostOptions.find(c => c.id === val);
                    if (costOption) costRate = costOption.value ?? '';
                  }

                  setValue('health_insurance_card.patient_cost_rate', costRate);
                }

                if (selectedValue.startsWith('ei-')) {
                  const val = Number(selectedValue.replace('ei-', ''));

                  setValue('health_insurance_card.elderly_insurance_certificate_id', val);
                  setValue('health_insurance_card.patient_cost_id', 0);

                  let costRate = '';
                  const elderlyOption = elderlyOptions.find(opt => opt.id === val);
                  if (elderlyOption) costRate = elderlyOption.value ?? '';

                  setValue('health_insurance_card.patient_cost_rate', costRate);
                }
              }}
              disabled={getFieldDisabled() || disabled}
            >
              <AppRadio value="pc-0">初期値</AppRadio>
              {patientCostOptions.map(cost => (
                <AppRadio key={cost.id} value={`pc-${cost.id}`}>
                  {cost.name}
                </AppRadio>
              ))}
              <br />
              {elderlyOptions
                .filter(e => e.id !== 1)
                .map(cert => (
                  <AppRadio key={cert.id} value={`ei-${cert.id}`}>
                    {cert.name}
                  </AppRadio>
                ))}
            </AppRadioGroup>
          </div>
        </div>

        <div className="col-span-2 grid grid-cols-[auto_minmax(0,1fr)_auto_minmax(0,1fr)] items-start gap-x-2 gap-y-2">
          <AppFormLabel label="記号" required />
          <div className="min-w-0">
            <Controller
              name="health_insurance_card.card_mark"
              control={control}
              render={({ field, fieldState }) => {
                const { ref: rhfRef, ...fieldProps } = field;
                return (
                  <AppForm.Item
                    className="min-w-0 mb-0"
                    validateStatus={fieldState.invalid ? 'error' : undefined}
                    help={fieldState.error?.message}
                  >
                    <AppTextField
                      {...fieldProps}
                      ref={instance => {
                        cardMarkInputRef.current = instance;
                        rhfRef(instance);
                      }}
                      value={field.value ?? ''}
                      maxLength={255}
                      className="w-full min-w-0"
                      disabled={getFieldReadonly('cardMark') || disabled}
                      onBlur={() => {
                        field.onBlur();
                        handleCardMarkBlur();
                      }}
                      status={fieldState.invalid ? 'error' : undefined}
                    />
                  </AppForm.Item>
                );
              }}
            />
          </div>

          <AppFormLabel label={`${getCardNumberLabel()}`} required />
          <div className="min-w-0">
            <Controller
              name="health_insurance_card.card_number"
              control={control}
              render={({ field, fieldState }) => {
                const { ref: rhfRef, ...fieldProps } = field;
                return (
                  <AppForm.Item
                    className="min-w-0 mb-0"
                    validateStatus={fieldState.invalid ? 'error' : undefined}
                    help={fieldState.error?.message}
                  >
                    <AppTextField
                      {...fieldProps}
                      ref={instance => {
                        cardNumberInputRef.current = instance;
                        rhfRef(instance);
                      }}
                      value={field.value ?? ''}
                      maxLength={255}
                      className="w-full min-w-0"
                      disabled={getFieldReadonly('cardNumber') || disabled}
                      status={fieldState.invalid ? 'error' : undefined}
                      onBlur={() => {
                        field.onBlur();
                        handleCardNumberBlur();
                      }}
                    />
                  </AppForm.Item>
                );
              }}
            />
          </div>

          <AppFormLabel label="資格取得" required />
          <div className="min-w-0">
            <Controller
              name="health_insurance_card.qualification_date"
              control={control}
              render={({ field, fieldState }) => (
                <AppForm.Item
                  className="min-w-0 mb-0"
                  validateStatus={fieldState.invalid ? 'error' : undefined}
                  help={fieldState.error?.message}
                >
                  <RA2NDatePicker
                    value={field.value ?? ''}
                    onChange={val => field.onChange(dateValueToIso(val) ?? '')}
                    className="w-full min-w-0"
                    placeholder="元号00年00月00日"
                    error={fieldState.invalid}
                    disabled={disabled}
                  />
                </AppForm.Item>
              )}
            />
          </div>

          <AppFormLabel label="有効期限" />
          <div className="min-w-0">
            <Controller
              name="health_insurance_card.expiration_date"
              control={control}
              render={({ field, fieldState }) => (
                <AppForm.Item
                  className="min-w-0 mb-0"
                  validateStatus={fieldState.invalid ? 'error' : undefined}
                  help={fieldState.error?.message}
                >
                  <RA2NDatePicker
                    value={field.value ?? ''}
                    onChange={val => field.onChange(dateValueToIso(val) ?? '')}
                    className="w-full min-w-0"
                    placeholder="元号00年00月00日"
                    error={fieldState.invalid}
                    disabled={disabled}
                  />
                </AppForm.Item>
              )}
            />
          </div>
        </div>
      </div>

      <div className="grid content-start grid-cols-[96px_minmax(0,1fr)] items-start gap-x-3 gap-y-2 sm:grid-cols-[140px_minmax(0,1fr)] sm:gap-x-6 xl:border-l xl:pl-6">
        <AppFormLabel
          label={
            <>
              被保険者
              <br />
              組合員世帯主
            </>
          }
          required
          className="!items-start whitespace-normal"
        />
        <div>
          <Controller
            name="health_insurance_card.name"
            control={control}
            render={({ field, fieldState }) => (
              <AppForm.Item
                className="mb-0"
                validateStatus={fieldState.invalid ? 'error' : undefined}
                help={fieldState.error?.message}
              >
                <AppTextField
                  {...field}
                  className="w-full sm:w-1/2"
                  disabled={isInsuredPerson || disabled}
                  maxLength={insuredNameKanaMaxLength}
                  status={fieldState.invalid ? 'error' : undefined}
                  {...insuredNameInputProps}
                />
              </AppForm.Item>
            )}
          />
        </div>

        <AppFormLabel label="被保険者名カナ" />
        <div>
          <Controller
            name="health_insurance_card.name_kana"
            control={control}
            render={({ field, fieldState }) => (
              <AppForm.Item
                className="mb-0"
                validateStatus={fieldState.invalid ? 'error' : undefined}
                help={fieldState.error?.message}
              >
                <AppTextField
                  {...field}
                  value={field.value ?? ''}
                  className="w-full sm:w-1/2"
                  maxLength={insuredNameKanaMaxLength}
                  disabled={isInsuredPerson || disabled}
                  status={fieldState.invalid ? 'error' : undefined}
                  {...insuredNameKanaInputProps}
                />
              </AppForm.Item>
            )}
          />
        </div>

        <AppFormLabel label="性別" />
        <div>
          <AppRadioGroup
            value={watch('health_insurance_card.sex_id')}
            onChange={e => setValue('health_insurance_card.sex_id', e.target.value)}
            disabled={isInsuredPerson || disabled}
          >
            {sexOptions.map(sex => (
              <AppRadio key={sex.id} value={sex.id}>
                {sex.name}
              </AppRadio>
            ))}
          </AppRadioGroup>
        </div>

        <AppFormLabel label="生年月日" />
        <div>
          <Controller
            name="health_insurance_card.date_of_birth"
            control={control}
            render={({ field, fieldState }) => (
              <AppForm.Item
                className="mb-0"
                validateStatus={fieldState.invalid ? 'error' : undefined}
                help={fieldState.error?.message}
              >
                <RA2NDatePicker
                  value={field.value ?? ''}
                  onChange={val => field.onChange(dateValueToIso(val) ?? '')}
                  className="w-40"
                  placeholder="元号00年00月00日"
                  disabled={isInsuredPerson || disabled}
                  error={fieldState.invalid}
                />
              </AppForm.Item>
            )}
          />
        </div>

        <AppFormLabel label="郵便番号" />
        <div className="grid grid-cols-[minmax(0,4.75rem)_minmax(0,4.75rem)_auto] items-center gap-x-2 gap-y-1">
          <Controller
            name="health_insurance_card.postal_code1"
            control={control}
            render={({ field, fieldState }) => (
              <AppTextField
                {...field}
                placeholder="000"
                inputMode="numeric"
                className="w-full min-w-0"
                disabled={isInsuredPerson || disabled}
                status={fieldState.invalid ? 'error' : undefined}
                onChange={e => {
                  const digits = normalizeInsurancePostalDigits(
                    (e.target as HTMLInputElement).value
                  );
                  const prevLen = prevInsurancePc1DigitsLenRef.current;
                  const pc1 = digits.slice(0, 3);
                  const overflow = digits.slice(3, 7);
                  prevInsurancePc1DigitsLenRef.current = pc1.length;
                  field.onChange(pc1);
                  if (overflow.length > 0) {
                    setValue('health_insurance_card.postal_code2', overflow.slice(0, 4), {
                      shouldDirty: true,
                      shouldValidate: false,
                    });
                    queueMicrotask(() => insurancePostalCode2Ref.current?.focus?.());
                  } else if (pc1.length === 3 && digits.length > prevLen) {
                    queueMicrotask(() => insurancePostalCode2Ref.current?.focus?.());
                  }
                }}
                onPaste={e => {
                  const clip = normalizeInsurancePostalDigits(
                    e.clipboardData.getData('text') || ''
                  );
                  if (clip.length < 2) return;
                  e.preventDefault();
                  const input = e.currentTarget;
                  const current = normalizeInsurancePostalDigits(input.value || '');
                  const selStart = input.selectionStart ?? current.length;
                  const selEnd = input.selectionEnd ?? current.length;
                  const merged =
                    `${current.slice(0, selStart)}${clip}${current.slice(selEnd)}`.replace(
                      /\D/g,
                      ''
                    );
                  const pc1 = merged.slice(0, 3);
                  const overflow = merged.slice(3, 7);
                  prevInsurancePc1DigitsLenRef.current = pc1.length;
                  field.onChange(pc1);
                  if (overflow.length > 0) {
                    setValue('health_insurance_card.postal_code2', overflow.slice(0, 4), {
                      shouldDirty: true,
                      shouldValidate: false,
                    });
                  }
                  queueMicrotask(() => insurancePostalCode2Ref.current?.focus?.());
                }}
                onBlur={e => {
                  field.onBlur();
                  handlePostalCode1Blur(e.target.value);
                }}
              />
            )}
          />
          <Controller
            name="health_insurance_card.postal_code2"
            control={control}
            render={({ field, fieldState }) => {
              const { ref: rhfRef, ...fieldProps } = field;
              return (
                <AppTextField
                  {...fieldProps}
                  ref={instance => {
                    insurancePostalCode2Ref.current = instance;
                    rhfRef(instance);
                  }}
                  placeholder="0000"
                  maxLength={4}
                  inputMode="numeric"
                  className="w-full min-w-0"
                  disabled={isInsuredPerson || disabled}
                  status={fieldState.invalid ? 'error' : undefined}
                  onChange={e => {
                    const normalized = normalizeInsurancePostalDigits(
                      (e.target as HTMLInputElement).value
                    ).slice(0, 4);
                    field.onChange(normalized);
                  }}
                  onBlur={e => {
                    field.onBlur();
                    handlePostalCode2Blur(e.target.value);
                  }}
                />
              );
            }}
          />
          <AppButton
            type="primary"
            className="justify-self-start"
            onClick={onAddressSearch}
            disabled={isInsuredPerson || disabled}
          >
            住所補助
          </AppButton>
          {(errors.health_insurance_card?.postal_code1?.message ||
            errors.health_insurance_card?.postal_code2?.message) && (
            <>
              <div className="min-w-0 self-start text-sm leading-tight text-[var(--ant-color-error)] break-words">
                {errors.health_insurance_card?.postal_code1?.message}
              </div>
              <div className="min-w-0 self-start text-sm leading-tight text-[var(--ant-color-error)] break-words">
                {errors.health_insurance_card?.postal_code2?.message}
              </div>
              <div className="min-w-0" aria-hidden />
            </>
          )}
        </div>

        <AppFormLabel label="住所" required />
        <div className="space-y-1">
          <div className="space-y-0.5">
            <div className="flex items-start gap-2">
              <Controller
                name="health_insurance_card.address1"
                control={control}
                render={({ field, fieldState }) => (
                  <AppForm.Item
                    className="flex-1 min-w-0 mb-0"
                    validateStatus={fieldState.invalid ? 'error' : undefined}
                    help={fieldState.error?.message}
                  >
                    <AppTextField
                      {...field}
                      className="flex-1"
                      maxLength={PATIENT_ADDRESS1_MAX_LENGTH}
                      disabled={isInsuredPerson || disabled}
                      status={fieldState.invalid ? 'error' : undefined}
                    />
                  </AppForm.Item>
                )}
              />
              <AppButton
                type="primary"
                className="self-start shrink-0"
                onClick={onOpenMap}
                data-skip-enter-nav
                disabled={disabled}
              >
                地図
              </AppButton>
            </div>
          </div>
          <div className="space-y-0.5">
            <Controller
              name="health_insurance_card.address2"
              control={control}
              render={({ field, fieldState }) => {
                const { ref: rhfRef, ...fieldProps } = field;
                return (
                  <AppForm.Item
                    className="mb-0"
                    validateStatus={fieldState.invalid ? 'error' : undefined}
                    help={fieldState.error?.message}
                  >
                    <AppTextField
                      {...fieldProps}
                      ref={instance => {
                        if (address2Ref) address2Ref.current = instance;
                        rhfRef(instance);
                      }}
                      maxLength={PATIENT_ADDRESS2_MAX_LENGTH}
                      readOnly={isInsuredPerson || disabled}
                      disabled={isInsuredPerson || disabled}
                      status={fieldState.invalid ? 'error' : undefined}
                      onChange={e => {
                        const next = e.target.value.slice(0, PATIENT_ADDRESS2_MAX_LENGTH);
                        field.onChange(next);
                      }}
                      onBlur={e => {
                        if (!isInsuredPerson) {
                          const val = (e.target as HTMLInputElement).value || '';
                          const normalized = normalizeAddress(val).slice(
                            0,
                            PATIENT_ADDRESS2_MAX_LENGTH
                          );
                          if (normalized !== val) {
                            setValue('health_insurance_card.address2', normalized, {
                              shouldDirty: true,
                              shouldTouch: true,
                              shouldValidate: false,
                            });
                          }
                        }
                        field.onBlur();
                      }}
                    />
                  </AppForm.Item>
                );
              }}
            />
          </div>
        </div>

        <AppFormLabel label="電話番号" />
        <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-start gap-x-2 gap-y-1">
          <Controller
            name="health_insurance_card.telephone"
            control={control}
            render={({ field, fieldState }) => (
              <AppForm.Item
                className="min-w-0 mb-0"
                validateStatus={fieldState.invalid ? 'error' : undefined}
                help={fieldState.error?.message}
              >
                <AppTextField
                  {...field}
                  maxLength={PATIENT_TELEPHONE_MAX_LENGTH}
                  className="w-full min-w-0"
                  disabled={isInsuredPerson || disabled}
                  status={fieldState.invalid ? 'error' : undefined}
                  onChange={e => {
                    const filtered = (e.target as HTMLInputElement).value
                      .replace(/[^0-9０-９\-－ー―ｰ]/g, '')
                      .slice(0, PATIENT_TELEPHONE_MAX_LENGTH);
                    field.onChange(filtered);
                  }}
                  onBlur={e => {
                    field.onBlur();
                    handleTelephoneBlur(e.target.value);
                  }}
                />
              </AppForm.Item>
            )}
          />
          <AppFormLabel label="住所カナ" className="px-1" />
          <Controller
            name="health_insurance_card.address_kana"
            control={control}
            render={({ field, fieldState }) => (
              <AppForm.Item
                className="min-w-0 mb-0"
                validateStatus={fieldState.invalid ? 'error' : undefined}
                help={fieldState.error?.message}
              >
                <AppTextField
                  {...field}
                  maxLength={PATIENT_ADDRESS_KANA_MAX_LENGTH}
                  className="w-full min-w-0"
                  disabled={isInsuredPerson || disabled}
                  status={fieldState.invalid ? 'error' : undefined}
                />
              </AppForm.Item>
            )}
          />
        </div>
      </div>
    </div>
  );
}
